import {
  INSURANCE_COVERAGE_CHECK_COUNTRIES,
  INSURANCE_COVERAGE_CHECK_PLAN_TYPES,
  InsuranceCoverageCheckCountry,
  InsuranceCoverageCheckEntityKind,
  InsuranceCoverageCheckInsuranceType,
  InsuranceCoverageCheckPlanType,
  SaveInsuranceCoverageCheckData,
} from 'hydra-data-types';
import { isNonEmptyString, stripUndefined } from 'ts-utils';
import { MedicareAccessScreenName } from '../config/FlowTypes';
import {
  COVERAGE_PLAN_TYPE_EMPLOYER,
  COVERAGE_PLAN_TYPE_MEDICAID,
  COVERAGE_PLAN_TYPE_SELF_PURCHASED,
  HEALTH_COVER_MEDICARE_ADVANTAGE,
  HEALTH_COVER_MEDICARE_ORIGINAL,
  NOT_LISTED_OPTION,
} from '../config/InsuranceProviders';
import { NervaControlScreenName } from '../config/nerva/NervaControlScreenNames';
import { StoreStateInternal } from '../store/Store';
import { UserDataState } from '../store/userData/UserDataState';
import { getCoverageAddress } from './CoverageAddress';

const YES = 'Yes';
const DATE_YMD = /^\d{4}-\d{2}-\d{2}$/;

/** The stored date of birth when it is in the `YYYY-MM-DD` form the callable accepts. */
function getDateOfBirth(userData: UserDataState): string | undefined {
  const dob = userData.dateOfBirth?.[0];
  return dob !== undefined && DATE_YMD.test(dob) ? dob : undefined;
}

/**
 * The coverage check is complete once the user authorised it, answered the address step (chose an
 * address or said they couldn't find it), and has a date of birth on file. Both flows ask date of
 * birth before the coverage screens, and the callable requires it alongside advocacy consent, so
 * without it the save would only be rejected. Self-payers never authorise, so they never qualify.
 */
export function isCoverageCheckComplete(state: StoreStateInternal): boolean {
  const { userData } = state;
  return (
    userData.coverageCheckAuthorization?.[0] === YES &&
    (getCoverageAddress(userData) !== undefined ||
      userData.coverageAddressNotFound !== undefined) &&
    getDateOfBirth(userData) !== undefined
  );
}

/** Control and Personalise store the provider dropdown under different screen names. */
function getProviderAnswer(userData: UserDataState): string | undefined {
  const answers = userData as Partial<Record<string, string[]>>;
  return (
    answers[NervaControlScreenName.InsuranceProvider]?.[0] ??
    answers[MedicareAccessScreenName.MedicareAccessInsuranceProvider]?.[0]
  );
}

function isPlanType(value: string | undefined): value is InsuranceCoverageCheckPlanType {
  return INSURANCE_COVERAGE_CHECK_PLAN_TYPES.includes(value as InsuranceCoverageCheckPlanType);
}

/** An Original Medicare dropdown pick skips the plan-type screen, so it stands in for the answer. */
function getPlanType(
  userData: UserDataState,
  provider: string | undefined
): InsuranceCoverageCheckPlanType {
  const answered = userData.coveragePlanType?.[0];
  if (isPlanType(answered)) return answered;
  return provider === HEALTH_COVER_MEDICARE_ORIGINAL ? HEALTH_COVER_MEDICARE_ORIGINAL : '';
}

const PUBLIC_PROGRAMS: string[] = [
  HEALTH_COVER_MEDICARE_ORIGINAL,
  HEALTH_COVER_MEDICARE_ADVANTAGE,
  COVERAGE_PLAN_TYPE_MEDICAID,
];

function getInsuranceType(
  planType: InsuranceCoverageCheckPlanType
): InsuranceCoverageCheckInsuranceType {
  if (planType === COVERAGE_PLAN_TYPE_EMPLOYER) return 'Employer-sponsored insurance';
  if (planType === COVERAGE_PLAN_TYPE_SELF_PURCHASED) return 'Private health insurance';
  if (PUBLIC_PROGRAMS.includes(planType)) return 'Public / government program';
  return '';
}

/** The payer entity the answers point at; the employer is the higher-value key for US advocacy. */
function getEntityKind(
  planType: InsuranceCoverageCheckPlanType,
  insurer: string
): InsuranceCoverageCheckEntityKind {
  if (planType === COVERAGE_PLAN_TYPE_EMPLOYER) return 'employer';
  if (PUBLIC_PROGRAMS.includes(planType)) return 'program';
  return insurer === '' ? '' : 'insurer';
}

function getCountry(country: string): InsuranceCoverageCheckCountry {
  return INSURANCE_COVERAGE_CHECK_COUNTRIES.includes(country as InsuranceCoverageCheckCountry)
    ? (country as InsuranceCoverageCheckCountry)
    : 'OTHER';
}

function consentGiven(answer: string[] | undefined): boolean | undefined {
  return answer === undefined ? undefined : answer[0] === YES;
}

/**
 * Maps the coverage-flow answers and account details onto the `/insurance_coverage_check` document
 * the website form also writes. Returns `undefined` when the flow isn't complete or there is no
 * account email to file it under. `id` is set once a document exists so the save updates it.
 * `sourcePath` is the flow config name (e.g. NervaControl), passed in so this module doesn't import
 * the flow configs.
 */
export function buildInsuranceCoverageCheck(
  state: StoreStateInternal,
  sourcePath: string
): SaveInsuranceCoverageCheckData | undefined {
  const { user, userData, geolocation } = state;
  if (!isCoverageCheckComplete(state) || !isNonEmptyString(user.email)) return undefined;

  const provider = getProviderAnswer(userData);
  // "Not listed" carries no payer; a curated dropdown pick counts as verified.
  const insurer = provider === undefined || provider === NOT_LISTED_OPTION ? '' : provider;
  const planType = getPlanType(userData, provider);
  // Only email accounts carry a last name; token users have just `name`.
  const lastName = 'lastName' in user ? user.lastName : undefined;

  const check: SaveInsuranceCoverageCheckData = {
    id: userData.insuranceCoverageCheckId?.[0],
    source: 'webAssessment',
    sourcePath,
    country: getCountry(geolocation.country),
    insurer,
    employer: userData.coverageUsEmployer?.[0] ?? '',
    entityKind: getEntityKind(planType, insurer),
    entityVerified: insurer !== '',
    name: [user.name, lastName].filter(isNonEmptyString).join(' '),
    planType,
    insuranceType: getInsuranceType(planType),
    coverDetail: (userData.coverageBenefits ?? []).join(', '),
    email: user.email,
    // Only authorised users get this far, and the authorisation covers contacting the plan.
    consent: true,
    advocacyConsent: true,
    dob: getDateOfBirth(userData) ?? '',
    memberId: '',
    workEmail: userData.coverageWorkEmail?.[0],
    healthPlanConsent: consentGiven(userData.coverageCheckAuthorization),
    assignmentOfBenefitsConsent: consentGiven(userData.coverageAssignmentOfBenefits),
    employerOutreachConsent: consentGiven(userData.coverageEmployerOutreachConsent),
    address: getCoverageAddress(userData),
  };
  // The callable's schema rejects unknown keys; undefined optionals must not be sent as keys.
  return stripUndefined(check);
}
